/**
 * COG-4755 — sticky Add to Cart bar: option selectors and quantity.
 *
 * Lives outside assets/theme.js on purpose. This is a new component rather than a change to existing Prestige
 * behaviour, so keeping it in its own file means a future Prestige update cannot silently overwrite it.
 *
 * Contract with the theme:
 *   - The main <variant-picker> owns variant state. This component never writes to the form's variant field.
 *   - `picker.product`, `picker.selectedVariant` and `picker.selectVariant(id)` are the public API used here.
 *   - The form's `variant:change` event is the sync bus that price-list, buy-buttons, variant-media and the
 *     sold-out badge already listen to; this component both listens to it and causes it.
 *   - The real `quantity` field belongs to the main form. The bar's stepper carries no name and drives it.
 *
 * Availability is mirrored from the main picker's own computed `.is-disabled` / `hidden` state on its
 * `[data-option-value]` elements rather than recalculated, so it stays consistent with COG-4744 by construction.
 */
if (!window.customElements.get('sticky-variant-selector')) {
  class StickyVariantSelector extends HTMLElement {
    connectedCallback() {
      this.form = document.forms[this.getAttribute('form')];

      if (!this.form) {
        return;
      }

      this.picker = document.querySelector('variant-picker[form="' + this.getAttribute('form') + '"]');

      if (!this.picker) {
        // Nothing to drive — leave the server-rendered state alone.
        return;
      }

      // One dropdown per product option, in option order.
      this.selects = Array.from(this.querySelectorAll('select[data-option-position]')).sort(
        (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
      );

      if (this.selects.length === 0) {
        return;
      }

      // Quantity is a sibling rather than a child, so it can share a row with the button on mobile.
      this.bar = this.closest('product-sticky-bar');
      this.quantityContainer = this.bar?.querySelector('.sticky-variant-selector__quantity');
      this.quantityInput = this.quantityContainer?.querySelector('input');
      this.mainQuantityInput = this.form.elements.quantity;

      this.onOptionChange = this.handleOptionChange.bind(this);
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onQuantityInteraction = this.handleQuantityInteraction.bind(this);

      this.addEventListener('change', this.onOptionChange);
      this.form.addEventListener('variant:change', this.onVariantChange);

      if (this.quantityInput && this.mainQuantityInput) {
        // stepUp()/stepDown() are native and dispatch no events, so listening for `change` alone would miss
        // the +/- buttons. The theme's listeners sit on the buttons themselves, so by the time these
        // container-level handlers run the value has already been stepped.
        this.quantityContainer.addEventListener('click', this.onQuantityInteraction);
        this.quantityContainer.addEventListener('input', this.onQuantityInteraction);

        // The bar is revealed by an IntersectionObserver toggling .is-visible. Whenever it appears, adopt
        // whatever quantity the shopper set in the main section, so the two never disagree on screen.
        if (this.bar) {
          this.barObserver = new MutationObserver(() => {
            if (this.bar.classList.contains('is-visible')) {
              this.adoptMainQuantity();
            }
          });

          this.barObserver.observe(this.bar, { attributes: true, attributeFilter: ['class'] });
        }

        this.adoptMainQuantity();
      }

      // The picker loads its product JSON asynchronously and may have dispatched its initial
      // variant:change before this element upgraded. Sync once on connect to cover that.
      this.syncAvailability();
      if (this.picker.product) {
        this.syncQuantityRule(this.picker.selectedVariant);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onOptionChange);
      this.quantityContainer?.removeEventListener('click', this.onQuantityInteraction);
      this.quantityContainer?.removeEventListener('input', this.onQuantityInteraction);
      this.form?.removeEventListener('variant:change', this.onVariantChange);
      this.barObserver?.disconnect();
    }

    handleOptionChange(event) {
      if (!event.target.matches('select[data-option-position]') || !this.picker.product) {
        return;
      }

      const wanted = this.selects.map((select) => select.value);
      const variant = this.picker.product.variants.find((candidate) =>
        candidate.options.every((value, index) => value === wanted[index])
      );

      if (variant) {
        // Everything downstream — price, button state, gallery, and this component's own display — updates
        // through the variant:change this dispatches.
        this.picker.selectVariant(variant.id);
      } else {
        // Combination does not exist. Put the dropdowns back to the variant actually selected.
        this.syncFromVariant(this.picker.selectedVariant);
      }
    }

    handleVariantChange(event) {
      this.syncFromVariant(event.detail.variant);
      this.syncAvailability();
      this.syncQuantityRule(event.detail.variant);
    }

    syncFromVariant(variant) {
      if (!variant) {
        return;
      }

      this.selects.forEach((select, index) => {
        const value = variant['option' + (index + 1)];

        if (value != null && select.value !== value) {
          select.value = value;
        }
      });
    }

    /**
     * Mirror the main picker's computed availability. Both render option values in the same order, so index
     * alignment is safe, and this avoids duplicating the picker's combination logic.
     */
    syncAvailability() {
      // Until the picker has loaded its product JSON it has not computed any availability, so mirroring here
      // would replace the correct server-rendered state with "everything available". Leave it alone.
      if (!this.picker.product) {
        return;
      }

      const optionSelectors = this.picker.querySelectorAll('[data-option-selector]');

      this.selects.forEach((select, index) => {
        const source = optionSelectors[index];

        if (!source) {
          return;
        }

        const sourceValues = source.querySelectorAll('[data-option-value]');

        Array.from(select.options).forEach((option, valueIndex) => {
          const sourceValue = sourceValues[valueIndex];

          if (!sourceValue) {
            return;
          }

          option.disabled = sourceValue.classList.contains('is-disabled');
          option.hidden = sourceValue.hasAttribute('hidden');
        });
      });
    }

    /** Keep the proxy stepper on the same min/max/increment contract as the submitted field. */
    syncQuantityRule(variant) {
      if (!this.quantityInput || !this.mainQuantityInput || !variant) {
        return;
      }

      // Normalize the submitted field first so listener ordering cannot copy a stale quantity into the proxy.
      this.mainQuantityInput.updateVariantRule?.(variant);
      this.quantityInput.updateVariantRule?.(variant);
      this.adoptMainQuantity();
      this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    /** Push the bar's quantity onto the real field that gets submitted. */
    handleQuantityInteraction() {
      const quantity = parseInt(this.quantityInput.value, 10);

      if (!Number.isNaN(quantity) && quantity > 0) {
        this.mainQuantityInput.value = quantity;
        this.mainQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /** Take the quantity from the main section, so the bar shows what will actually be added. */
    adoptMainQuantity() {
      const quantity = parseInt(this.mainQuantityInput.value, 10);

      if (Number.isNaN(quantity) || quantity < 1 || this.quantityInput.value === String(quantity)) {
        return;
      }

      this.quantityInput.value = quantity;

      // Deliberately not dispatching `input` here: it would bubble back into handleQuantityInteraction and
      // write straight back to the main field. Set the width custom property directly instead, which is all
      // the theme's own input listener does with it.
      this.quantityInput.style.setProperty('--quantity-selector-character-count', String(quantity).length + 'ch');
    }
  }

  window.customElements.define('sticky-variant-selector', StickyVariantSelector);
}
